import React, { useState, useEffect } from 'react';
import { fetchCards, addCard, fetchDefinitions, updateCardScores } from 'api';
import { Modal, SettingsButton, SettingsControls, WordCard, NavigationButtons, WordInputRow, TextSuggestionPanel, BackgroundWrapper, Spinner, ImportantButton, FlipIcon } from 'components';
import { useExtendedMutation } from 'mutations';
import { useAuth } from "react-oidc-context";
import { useSessionStats, useAccountStats } from 'context';
import './FlashcardPage.css';

const FlashcardPage = () => {
  const auth = useAuth();
  
  const [openModal, setOpenModal] = useState(false);
  const [words, setWords] = useState([]);
  const [wordIndex, setWordIndex] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [userTextInput, setUserTextInput] = useState('');
  const [newWord, setNewWord] = useState('');
  const [isAddingCard, setIsAddingCard] = useState(false);
  const [translationEnabled, setTranslationEnabled] = useState(false);
  const [panelOpen, setPanelOpen] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [flipped, setFlipped] = useState(false);
  const [hasBeenFlipped, setHasBeenFlipped] = useState(false);
  const [pendingOutcomes, setPendingOutcomes] = useState([]);
  const [isDesktop, setIsDesktop] = useState(() => window.innerWidth > 768);
  const { setSessionResults } = useSessionStats();
  const { incrementWordCount } = useAccountStats();

  const token = auth.user?.access_token;

  const getCardsSuccessCallback = (data) => {
    console.log(data[0]);
    setWords(data);
    setIsLoading(false);
    setSessionResults(Object.fromEntries(data.map(card => [card.word, null])));
  }

  const addCardSuccessCallback = (data) => {
    setNewWord('');
    setIsAddingCard(false);
    setPanelOpen(false);
    setSuggestions([]);
    console.log([...words, data]);
    setWords([...words, data]);
    incrementWordCount();
  }

  const errorCallback = (error) => {
    console.log(error); //TODO add proper error handling
  }

  const getCardsMutation = useExtendedMutation(fetchCards, getCardsSuccessCallback, errorCallback);
  const addCardMutation = useExtendedMutation(addCard, addCardSuccessCallback, errorCallback);
  const updateCardScoresMutation = useExtendedMutation(updateCardScores, () => {}, errorCallback);
  const fetchDefinitionsMutation = useExtendedMutation(
    fetchDefinitions,
    (data) => setSuggestions(data),
    () => setSuggestions([])
  );

  useEffect(() => {
    const check = () => setIsDesktop(window.innerWidth > 768);
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);

  useEffect(() => {
    if (auth.isAuthenticated && auth.user) {
      setIsLoading(true);
      getCardsMutation.mutateOnVirtualThread({ token, limitByDueDate: true });
    }
  }, [auth.isAuthenticated, auth.user]);

  const incrementWordIndex = () => {
    setFlipped(false);
    setHasBeenFlipped(false);
    setWordIndex((prevIndex) => (prevIndex + 1) % words.length);
  };

  const updateSessionResults = (word, success) => {
    setSessionResults(prev => {
      if (prev[word] === 'wrong') return prev; // wrong is sticky
      return { ...prev, [word]: success ? 'correct' : 'wrong' };
    });
  };

  const recordOutcome = (success) => {
    const currentWord = words[wordIndex];
    updateSessionResults(currentWord.word, success);
    incrementWordIndex();
    const outcome = {
      card: { subcategoryId: currentWord.subcategoryId, word: currentWord.word },
      success,
    };
    const next = [...pendingOutcomes, outcome];
    if (next.length >= 5) {
      setPendingOutcomes([]);
      setTimeout(() => updateCardScoresMutation.mutateOnVirtualThread({ token, outcomes: next }), 0);
    } else {
      setPendingOutcomes(next);
    }
  };

  const handleAddWord = () => {
    if (userTextInput.trim() !== '') {
      if (newWord) {
        setIsAddingCard(true);
        addCardMutation.mutateOnVirtualThread({word: newWord, definition: userTextInput, token: token});
      }
      else {
        setNewWord(userTextInput);
        setSuggestions([]);
        if (translationEnabled) {
          fetchDefinitionsMutation.mutateOnVirtualThread({ word: userTextInput, token });
          setPanelOpen(true);
        }
      }
      setUserTextInput('');
    }
  };

  const handleInputChange = (event) => {
    setUserTextInput(event.target.value);
  };

  const handleInputKeyPress = (event) => {
    if (event.key === 'Enter') {
      handleAddWord();
    }
  };

  if (auth.isAuthenticated && auth.user) {
      console.log('auth.user', auth.user);
      const token = auth.user?.access_token;

      return (
        <>
          {!isDesktop && <SettingsButton onClick={() => setOpenModal(true)} />}
          {!isDesktop && (
            <Modal isOpen={openModal} onClose={() => setOpenModal(false)} onClick={() => {}}>
              <SettingsControls token={token} onClose={() => setOpenModal(false)} onApplied={() => window.location.reload()} />
            </Modal>
          )}
          {isLoading && <Spinner />}
          <div style={{ display: isLoading ? 'none' : 'contents' }}>
            <BackgroundWrapper>
              <WordCard word={flipped ? words[wordIndex]?.definition : words[wordIndex]?.word}/>
              {!flipped && words[wordIndex]?.sentences?.[0] && (
                <p className="sentence-context">{words[wordIndex].sentences[0]}</p>
              )}
              <ImportantButton onClick={() => { setFlipped(prev => !prev); setHasBeenFlipped(true); }}>
                <FlipIcon />
              </ImportantButton>
              <TextSuggestionPanel
                visible={panelOpen}
                onClose={() => setPanelOpen(false)}
                onAccept={(text) => setUserTextInput(text)}
                suggestions={suggestions}
                suggestionsLoading={fetchDefinitionsMutation.isPending}
              />
              <WordInputRow
                value={userTextInput}
                callbackOnChange={handleInputChange}
                callbackOnKeyDown={handleInputKeyPress}
                placeholder={newWord ? "Add definition" : "Add word"}
                maxLength={newWord ? 100 : 35}
                loading={isAddingCard}
                translationEnabled={translationEnabled}
                onToggleTranslation={() => {
                  const nowEnabled = !translationEnabled;
                  setTranslationEnabled(nowEnabled);
                  if (nowEnabled && newWord && suggestions.length === 0 && !fetchDefinitionsMutation.isPending) {
                    fetchDefinitionsMutation.mutateOnVirtualThread({ word: newWord, token });
                    setPanelOpen(true);
                  }
                }}
                suggestionsOpen={panelOpen}
                onToggleSuggestions={newWord ? () => setPanelOpen(prev => !prev) : undefined}
              />
              <NavigationButtons
                callbackLeft={() => recordOutcome(false)}
                callbackRight={() => recordOutcome(true)}
                rightDisabled={hasBeenFlipped}
              />
            </BackgroundWrapper>
          </div>
        </>
      )
  } else {
    return (<div></div>)
  }
};

export { FlashcardPage }